/* 
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
 * Distributed under the TruVideo proprietary license. 
 */
import { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';

// Slide enter/exit variants — direction drives which side slides in/out from.
// direction > 0  → navigating forward  (enter from right, exit to left)
// direction < 0  → navigating backward (enter from left, exit to right)
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const springTransition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
  mass: 0.8,
};

/**
 * PhotoGallery — swipeable carousel for multi-photo review steps.
 *
 * Props:
 *   photos        — array of photo objects
 *   activeIndex   — currently visible slide index (controlled)
 *   onIndexChange — called with new index when user swipes or taps a dot / arrow
 *   renderSlide   — (photo) => ReactNode  renders the media content for each slide
 *   canPreview    — whether tapping the slide opens the fullscreen viewer
 *   onPreview     — (photo) => void  called when user taps to preview (not a swipe)
 */
const PhotoGallery = ({ photos, activeIndex, onIndexChange, renderSlide, canPreview, onPreview }) => {
  const prevIndexRef = useRef(activeIndex);
  const directionRef = useRef(0);
  // draggedRef prevents a swipe motion from also triggering the tap-to-preview onClick
  const draggedRef = useRef(false);
  const total = photos.length;

  // Compute direction synchronously during render (not in a useEffect): the
  // slide swap below reads it in this same render via key={activeIndex}, so a
  // value that only updates a render later would make the new slide enter
  // from the wrong side on every swipe.
  if (activeIndex !== prevIndexRef.current) {
    directionRef.current = activeIndex > prevIndexRef.current ? 1 : -1;
    prevIndexRef.current = activeIndex;
  }
  const direction = directionRef.current;

  const goTo = (newIndex) => {
    if (newIndex < 0 || newIndex >= total || newIndex === activeIndex) return;
    onIndexChange(newIndex);
  };

  const goNext = () => goTo(activeIndex + 1);
  const goPrev = () => goTo(activeIndex - 1);

  const activePhoto = photos[activeIndex];

  return (
    <div className="relative w-full h-full overflow-hidden">

      {/* ── Slides ── */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={activeIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={springTransition}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.1}
          onDragStart={() => { draggedRef.current = false; }}
          onDrag={(_, info) => {
            // Mark as a real drag once offset exceeds 5px
            if (Math.abs(info.offset.x) > 5) draggedRef.current = true;
          }}
          onDragEnd={(_, info) => {
            if (info.offset.x < -50 && activeIndex < total - 1) goNext();
            else if (info.offset.x > 50 && activeIndex > 0) goPrev();
          }}
          className="absolute inset-0 w-full h-full select-none"
          // pan-y: let vertical scroll work normally while we capture horizontal drag
          style={{ touchAction: 'pan-y', cursor: 'grab' }}
          whileDrag={{ cursor: 'grabbing' }}
        >
          {renderSlide(activePhoto)}

          {/* Tap-to-preview overlay — only fires if the user did NOT drag */}
          {canPreview && (
            <div
              className="absolute inset-0 bg-black/5 flex items-center justify-center"
              onClick={() => {
                if (draggedRef.current) {
                  draggedRef.current = false;
                  return;
                }
                onPreview?.(activePhoto);
              }}
            >
              <div className="bg-primary/90 backdrop-blur-md px-4 py-2 rounded-full text-primary-content font-black text-[10px] uppercase tracking-widest shadow-lg pointer-events-none">
                Tap to preview
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* ── Photo counter badge — top right ── */}
      <div className="absolute top-2 right-2 z-20 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full pointer-events-none select-none">
        {activeIndex + 1} / {total}
      </div>

      {/* ── Prev arrow — hidden on mobile, visible on sm+ ── */}
      {activeIndex > 0 && (
        <button
          type="button"
          onClick={goPrev}
          className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white items-center justify-center hover:bg-black/70 transition-colors active:scale-95"
          aria-label="Previous photo"
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
        </button>
      )}

      {/* ── Next arrow — hidden on mobile, visible on sm+ ── */}
      {activeIndex < total - 1 && (
        <button
          type="button"
          onClick={goNext}
          className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white items-center justify-center hover:bg-black/70 transition-colors active:scale-95"
          aria-label="Next photo"
        >
          <ChevronRight size={18} strokeWidth={2.5} />
        </button>
      )}

      {/* ── Dot indicators — bottom center ── */}
      <div className="absolute bottom-2 left-0 right-0 z-20 flex items-center justify-center gap-1.5">
        {photos.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to photo ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
              i === activeIndex
                ? 'w-4 h-1.5 bg-white shadow-sm'
                : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

PhotoGallery.propTypes = {
  photos: PropTypes.array.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  renderSlide: PropTypes.func.isRequired,
  canPreview: PropTypes.bool,
  onPreview: PropTypes.func,
};

export default PhotoGallery;
